import { Undef } from 'types/UtilityTypes';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';

import { LOAD_VIEW_PIPELINES, QUOTE_VIEW_PIPELINES } from '../constants';
import { KitPipelineCounts } from './kitSuggestionCounts';

// Sidebar context for found-parts / formal-count scoping.
export enum KitSidebarView {
  Quote = 'quote',
  Load = 'load',
  All = 'all',
}

/**
 * Builds the human-readable "I found ..." fragments for the Kit message bubble.
 *
 * Scoped to the current sidebar view:
 * - Quote: Quick Quote / Load Building / Carrier Quote
 * - Load: Carrier Info / Appointment / Check Call
 */
export function buildKitFoundParts({
  view = KitSidebarView.All,
  countsByPipeline = {},
  preloadedCarrierQuoteCount = 0,
}: {
  view?: KitSidebarView;
  countsByPipeline?: KitPipelineCounts;
  preloadedCarrierQuoteCount?: number;
}): string[] {
  const includeQuotePipelines =
    view === KitSidebarView.Quote || view === KitSidebarView.All;
  const includeLoadPipelines =
    view === KitSidebarView.Load || view === KitSidebarView.All;

  const foundParts: string[] = [];

  const quickQuoteCount = countOf(
    countsByPipeline,
    SuggestionPipelines.QuickQuote
  );
  const loadBuildingCount = countOf(
    countsByPipeline,
    SuggestionPipelines.LoadBuilding
  );
  const carrierInfoCount = countOf(
    countsByPipeline,
    SuggestionPipelines.CarrierInfo
  );
  const apptConfirmationCount = countOf(
    countsByPipeline,
    SuggestionPipelines.ApptConfirmation
  );
  const checkCallCount = countOf(
    countsByPipeline,
    SuggestionPipelines.CheckCall
  );

  if (includeQuotePipelines && quickQuoteCount > 0) {
    foundParts.push(
      quickQuoteCount === 1
        ? '1 lane to quote'
        : `${quickQuoteCount} lanes to quote`
    );
  }
  if (includeQuotePipelines && loadBuildingCount > 0) {
    foundParts.push(
      `${loadBuildingCount} load${loadBuildingCount > 1 ? 's' : ''} to build`
    );
  }
  if (includeLoadPipelines && carrierInfoCount > 0) {
    foundParts.push(
      carrierInfoCount === 1
        ? '1 carrier update'
        : `${carrierInfoCount} carrier updates`
    );
  }
  if (includeLoadPipelines && apptConfirmationCount > 0) {
    foundParts.push(
      apptConfirmationCount === 1
        ? '1 appointment update'
        : `${apptConfirmationCount} appointment updates`
    );
  }
  if (includeLoadPipelines && checkCallCount > 0) {
    foundParts.push(
      checkCallCount === 1 ? '1 check call' : `${checkCallCount} check calls`
    );
  }
  if (includeQuotePipelines && preloadedCarrierQuoteCount > 0) {
    foundParts.push(
      preloadedCarrierQuoteCount === 1
        ? '1 carrier quote preloaded'
        : `${preloadedCarrierQuoteCount} carrier quotes preloaded`
    );
  }

  return foundParts;
}

function hasAnyPipelineCount(
  countsByPipeline: KitPipelineCounts,
  pipelines: readonly SuggestionPipelines[]
): boolean {
  return pipelines.some((pipeline) => countOf(countsByPipeline, pipeline) > 0);
}

// True when Kit has email suggestions for the given view (not preload alone).
export function hasKitSuggestionCounts(
  countsByPipeline: KitPipelineCounts,
  view: KitSidebarView = KitSidebarView.All
): boolean {
  const hasQuoteSuggestions = hasAnyPipelineCount(
    countsByPipeline,
    QUOTE_VIEW_PIPELINES
  );
  const hasLoadSuggestions = hasAnyPipelineCount(
    countsByPipeline,
    LOAD_VIEW_PIPELINES
  );

  if (view === KitSidebarView.Quote) {
    return hasQuoteSuggestions;
  }
  if (view === KitSidebarView.Load) {
    return hasLoadSuggestions;
  }
  return hasQuoteSuggestions || hasLoadSuggestions;
}

function countOf(
  countsByPipeline: Undef<KitPipelineCounts>,
  pipeline: SuggestionPipelines
): number {
  return countsByPipeline?.[pipeline] ?? 0;
}
